import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useSelector } from 'react-redux';

// Fonctions utilitaires partagées
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusBadge = (status) => {
  const statusConfig = {
    pending: { color: 'bg-yellow-100 text-yellow-800', text: 'À traiter' },
    approved: { color: 'bg-blue-100 text-blue-800', text: 'Approuvée' },
    exported: { color: 'bg-indigo-100 text-indigo-800', text: 'Exportée au robot' },
    installed: { color: 'bg-green-100 text-green-800', text: 'Installée' },
    rejected: { color: 'bg-red-100 text-red-800', text: 'Rejetée' }
  };

  const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status };
  return (
    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
      {config.text}
    </span>
  );
};

const AdminLicenseRequests = () => {
  const [licenseRequests, setLicenseRequests] = useState([]);
  const [pagination, setPagination] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const auth = useSelector((state) => state.auth);
  const { userInfo } = auth;

  useEffect(() => {
    fetchLicenseRequests();
  }, [currentPage, statusFilter]);

  // Auto-refresh toutes les 30 secondes si activé
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchLicenseRequests();
      setLastRefresh(new Date());
    }, 30000); // 30 secondes

    return () => clearInterval(interval);
  }, [autoRefresh, currentPage, statusFilter]);

  const fetchLicenseRequests = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get('/admin/license-requests', {
        params: { page: currentPage, limit: 20, status: statusFilter },
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      setLicenseRequests(data.licenseRequests);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProcessRequest = async (requestId, status, adminNotes) => {
    try {
      const { data } = await api.put(`/admin/license-requests/${requestId}`, { status, adminNotes }, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      
      // Update the request in the list
      setLicenseRequests(licenseRequests.map(request => 
        request._id === requestId ? { ...request, ...data.licenseRequest } : request
      ));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleManualExport = async (requestId) => {
    try {
      const { data } = await api.post(`/admin/license-requests/${requestId}/export`, {}, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });

      setLicenseRequests(licenseRequests.map(request => 
        request._id === requestId ? { ...request, ...data.licenseRequest } : request
      ));
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Demandes de Licence</h2>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            Total: {pagination.totalItems || 0} demandes
          </div>
          <button
            onClick={() => {
              fetchLicenseRequests();
              setLastRefresh(new Date());
            }}
            className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
            title="Rafraîchir la liste"
          >
            <i className="fas fa-sync-alt mr-1"></i>
            Actualiser
          </button>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>Auto-refresh (30s)</span>
          </label>
          {autoRefresh && (
            <span className="text-xs text-gray-500">
              Dernière MAJ: {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">À traiter</option>
              <option value="approved">Approuvées</option>
              <option value="exported">Exportées au robot</option>
              <option value="installed">Installées</option>
              <option value="rejected">Rejetées</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setStatusFilter('');
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* License Requests Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Liste des Demandes</h3>
        </div>
        
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-400">
            <p className="text-red-700">Erreur: {error}</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Machine ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {licenseRequests.map((request) => (
                <tr key={request._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {request.user?.name || 'Utilisateur inconnu'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {request.user?.email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {request.product?.name || 'Produit inconnu'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 font-mono">
                      {request.machineIdName || 'Non spécifié'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(request.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(request.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                    <LicenseRequestModal 
                      request={request} 
                      onProcess={handleProcessRequest} 
                    />
                    <button
                      onClick={() => handleManualExport(request._id)}
                      className="text-indigo-600 hover:text-indigo-900"
                      title="Envoyer/renvoyer la licence au VPS"
                    >
                      Exporter au VPS
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {pagination.current} sur {pagination.total}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === pagination.total}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Suivant
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Modal de traitement des demandes de licence
const LicenseRequestModal = ({ request, onProcess }) => {
  const [isOpen, setIsOpen] = useState(false);
  // Initialiser le statut avec le statut actuel de la demande, ou 'approved' par défaut
  const [status, setStatus] = useState('approved');
  const [adminNotes, setAdminNotes] = useState('');

  // Réinitialiser le state quand on ouvre le modal
  const handleOpen = () => {
    setIsOpen(true);
    // Réinitialiser avec le statut actuel de la demande
    setStatus(request.status === 'pending' ? 'approved' : request.status || 'approved');
    setAdminNotes(request.adminNotes || '');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onProcess(request._id, status, adminNotes);
    setIsOpen(false);
  };

  return (
    <>
      <button
        onClick={handleOpen}
        className="text-blue-600 hover:text-blue-900"
      >
        Traiter
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Traiter la demande de licence
              </h3>
              
              <div className="mb-4 p-3 bg-gray-50 rounded-md space-y-2">
                <p className="text-sm text-gray-600">
                  <strong>Utilisateur:</strong> {request.user?.name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Email:</strong> {request.user?.email}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Produit:</strong> {request.product?.name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Machine ID:</strong> {request.machineIdName || 'Non spécifié'}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Statut actuel:</strong> {getStatusBadge(request.status)}
                </p>
                {request.installedAt && (
                  <p className="text-sm text-green-600">
                    <strong>✅ Installée le:</strong> {formatDate(request.installedAt)}
                  </p>
                )}
                {request.exportedAt && (
                  <p className="text-sm text-blue-600">
                    <strong>📤 Exportée le:</strong> {formatDate(request.exportedAt)}
                  </p>
                )}
                {request.vendorResponse && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-xs font-semibold text-blue-800 mb-1">Message VPS:</p>
                    <p className="text-xs text-blue-700">{request.vendorResponse}</p>
                  </div>
                )}
                {request.licenseKey && (
                  <p className="text-sm text-gray-600 font-mono">
                    <strong>Clé de licence:</strong> {request.licenseKey}
                  </p>
                )}
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                  >
                    <option value="approved">Approuver</option>
                    <option value="exported">Marquer exportée</option>
                    <option value="installed">Marquer installée</option>
                    <option value="rejected">Rejeter</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes (optionnel)</label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ajouter des notes sur cette décision..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md ${
                      status === 'approved' 
                        ? 'bg-green-600 hover:bg-green-700'
                        : status === 'installed'
                        ? 'bg-green-600 hover:bg-green-700'
                        : status === 'exported'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {status === 'approved' 
                      ? 'Approuver'
                      : status === 'installed'
                      ? 'Marquer installée'
                      : status === 'exported'
                      ? 'Marquer exportée'
                      : 'Rejeter'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminLicenseRequests; 